import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client/core';
import { createUploadLink } from 'apollo-upload-client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import type { GraphQLError } from 'graphql';
import queryString from 'query-string';

import currentSiteMapper from '@/config/currentSiteMapper';
import { reportHttpError } from '@/config/sentry/helpers';

const graphqlApiEndpoint: string | undefined = import.meta.env
  .VITE_GRAPHQL_API_ENDPOINT;
const graphqlFileApiEndpoint: string | undefined = import.meta.env
  .VITE_GRAPHQL_FILE_API_ENDPOINT;

async function fetchWithoutLateAbort(uri: string, options: RequestInit) {
  const { signal, ...fetchOptions } = options;
  const controller = new AbortController();
  const forwardAbort = () => controller.abort(signal?.reason);
  if (signal?.aborted) forwardAbort();
  signal?.addEventListener('abort', forwardAbort);

  try {
    const response = await fetch(uri, {
      ...fetchOptions,
      signal: controller.signal,
    });
    const body = await response.text();
    return new Response(body, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    });
  } finally {
    signal?.removeEventListener('abort', forwardAbort);
  }
}

function customFetch(uri: string, options: RequestInit) {
  const gqlParams = JSON.parse(options.body as string);
  const queryParams = {
    operationName: gqlParams.operationName,
  };
  uri += `?${queryString.stringify(queryParams)}`;
  return fetchWithoutLateAbort(uri, options);
}

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const { operationName } = operation;
  const response: Response | undefined = operation.getContext().response;
  const common = {
    source: 'graphql' as const,
    method: 'POST',
    url: response?.url,
    status: response?.status,
    headers: response?.headers,
    operationName,
    callSite: operation.getContext().callSite as string | undefined,
  };

  if (networkError) {
    if (networkError.name === 'AbortError') return;
    const status = (networkError as { statusCode?: number }).statusCode;
    const result = (networkError as { result?: { errors?: GraphQLError[] } })
      .result;
    const detail = result?.errors?.[0]?.message ?? networkError.message;
    reportHttpError({
      ...common,
      status: status ?? common.status,
      message: `GraphQL network error in ${operationName}: ${detail}`,
      fingerprint: ['graphql-network-error', operationName, String(status)],
      responseData: result,
      level: status && status < 500 ? 'warning' : 'error',
      cause: networkError,
    });
    return;
  }

  graphQLErrors?.forEach(({ message, path, extensions }) => {
    reportHttpError({
      ...common,
      message: `GraphQL error in ${operationName}: ${message}`,
      fingerprint: ['graphql-error', operationName, message],
      responseData: { message, path, extensions },
    });
  });
});

interface ApolloClientBaseOption {
  fetch: (uri: string, options: RequestInit) => Promise<Response>;
}

class ApolloClientBase {
  endpoint: string | undefined;
  options: ApolloClientBaseOption;
  cache: InMemoryCache;
  backendBaseUrl: string;

  constructor() {
    this.endpoint = graphqlApiEndpoint;
    this.options = {
      fetch: customFetch,
    };

    this.cache = new InMemoryCache();

    this.backendBaseUrl = currentSiteMapper();
  }

  get authLink() {
    return setContext((_, { headers }) => {
      const token =
        typeof window !== 'undefined'
          ? localStorage.getItem('access_token')
          : null;

      return {
        headers: {
          ...headers,
          ...(token && { authorization: `Bearer ${token}` }),
        },
      };
    });
  }

  get backendUrl() {
    const url = this.backendBaseUrl;
    return `https://${url}/${this.endpoint}/`;
  }

  getLinkObject(): HttpLink | ApolloLink {
    return new HttpLink(
      Object.assign(
        {
          uri: this.backendUrl,
          credentials: 'same-origin',
        },
        this.options,
      ),
    );
  }

  getApolloClient() {
    const link = ApolloLink.from([
      errorLink,
      this.authLink,
      this.getLinkObject(),
    ]);
    return new ApolloClient({ link, cache: this.cache });
  }
}

class ApolloClientUpload extends ApolloClientBase {
  constructor() {
    super();
    this.endpoint = graphqlFileApiEndpoint;
  }
  getLinkObject() {
    return createUploadLink(
      Object.assign({
        uri: this.backendUrl,
        credentials: 'same-origin',
        fetch: fetchWithoutLateAbort,
      }),
    );
  }
}

export const apolloClient = new ApolloClientBase().getApolloClient();
export const apolloUploadClient = new ApolloClientUpload().getApolloClient();

export default ApolloClientBase;
